import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { Button, Card } from '@/components/UI';
import { trpc } from '@/api/trpc.ts';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import CompareFiltersCard from './experimentCompare/CompareFiltersCard';
import { PAGE_SIZE } from './experimentCompare/constants';
import {
    buildComparePageSearchParams,
    formatStrategyLabel,
    keyForRow,
    parseComparePageState,
    stagePresenceToBoolean
} from './experimentCompare/helpers';
import PerformanceTableCard from './experimentCompare/PerformanceTableCard';
import PinnedComparisonCard from './experimentCompare/PinnedComparisonCard';
import type { ComparePageState, CompareRow, CompareSortBy } from './experimentCompare/types';

const ExperimentCompare: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [, setSearchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [pinnedRows, setPinnedRows] = useState<CompareRow[]>([]);
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [pageState, setPageState] = useState<ComparePageState>(() => parseComparePageState(searchParams));
    const pageStateRef = useRef<ComparePageState>(pageState);

    useEffect(() => {
        setPageState(parseComparePageState(searchParams));
    }, [searchParams]);

    useEffect(() => {
        pageStateRef.current = pageState;
    }, [pageState]);

    const {
        selectedStrategyId,
        compareMode,
        selectedDuration,
        selectedHorizonSeconds,
        search,
        optionsFilter,
        hasScorecardsFilter,
        strategyHasBacktestsFilter,
        strategyHasPortfolioBacktestsFilter,
        strategyHasPortfolioFilter,
        strategyIsCurrentChampionFilter,
        minTotalEntries,
        minTotalEntities,
        currentPage,
        sortBy,
        sortDirection,
        hiddenColumns
    } = pageState;

    const defaultSortBy: CompareSortBy = selectedStrategyId === null ? 'avgAlphaPercent' : 'avgProfitPercent';

    const updateParams = useCallback(
        (updates: Record<string, string | null>, options?: { resetPage?: boolean }) => {
            const next = buildComparePageSearchParams(pageStateRef.current);

            for (const [key, value] of Object.entries(updates)) {
                if (value === null || value === '') {
                    next.delete(key);
                } else {
                    next.set(key, value);
                }
            }

            if (options?.resetPage !== false && !('page' in updates)) {
                next.set('page', '1');
            }

            const nextState = parseComparePageState(next);
            pageStateRef.current = nextState;
            setPageState(nextState);
            setSearchParams(next, { replace: true });
        },
        [setSearchParams]
    );

    const backToExperimentsUrl = selectedStrategyId ? `/user/experiments?strategyId=${selectedStrategyId}` : '/user/experiments';
    const selectedStrategyQuery = selectedStrategyId ? `?strategyId=${selectedStrategyId}` : '';

    const { strategies, query: strategiesQuery } = useTradingStrategies({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load strategies',
            showErrorToast: true,
            setError
        }
    });

    const compareFilterOptionsQuery = useAppQuery(
        {
            ...trpc.experiment.getCompareFilterOptions.queryOptions({
                strategyId: selectedStrategyId
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load compare filters',
            showErrorToast: true,
            setError
        }
    );

    const includeOptionsValue = optionsFilter === 'with' ? true : optionsFilter === 'without' ? false : null;
    const compareQueryEnabled = true;

    const compareQuery = useAppQuery(
        {
            ...trpc.experiment.compareMany.queryOptions({
                strategyId: selectedStrategyId,
                mode: compareMode,
                duration: selectedDuration,
                horizonSeconds: selectedHorizonSeconds,
                search: search.trim() ? search : null,
                includeOptions: includeOptionsValue,
                hasScorecards: stagePresenceToBoolean(hasScorecardsFilter),
                strategyHasBacktests: stagePresenceToBoolean(strategyHasBacktestsFilter),
                strategyHasPortfolioBacktests: stagePresenceToBoolean(strategyHasPortfolioBacktestsFilter),
                strategyHasPortfolio: stagePresenceToBoolean(strategyHasPortfolioFilter),
                strategyIsCurrentChampion: stagePresenceToBoolean(strategyIsCurrentChampionFilter),
                minTotalEntries,
                minTotalEntities,
                sortBy,
                sortDirection,
                limit: PAGE_SIZE,
                offset: (currentPage - 1) * PAGE_SIZE
            }),
            enabled: compareQueryEnabled,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load experiment comparison results',
            showErrorToast: true,
            setError
        }
    );

    const selectedStrategy = useMemo(() => {
        return strategies.find((strategy) => strategy.id === selectedStrategyId) ?? null;
    }, [selectedStrategyId, strategies]);

    const compareFilters = compareFilterOptionsQuery.data;
    const availableDurations = compareFilters?.durations ?? [];
    const durationHorizonMap = compareFilters?.durationHorizonMap ?? {};
    const availableHorizonsForDuration = selectedDuration ? (durationHorizonMap[selectedDuration] ?? []) : (compareFilters?.horizons ?? []);
    const rows = compareQuery.data?.rows ?? [];

    useEffect(() => {
        if (!compareFilters) {
            return;
        }

        if (compareMode === 'fixed') {
            if (selectedDuration && !availableDurations.includes(selectedDuration)) {
                updateParams({ duration: null }, { resetPage: false });
                return;
            }

            const horizons = selectedDuration ? (durationHorizonMap[selectedDuration] ?? []) : (compareFilters.horizons ?? []);

            if (selectedHorizonSeconds !== null && !horizons.includes(selectedHorizonSeconds)) {
                updateParams({ horizonSeconds: null }, { resetPage: false });
            }

            return;
        }

        if (selectedHorizonSeconds !== null) {
            updateParams({ horizonSeconds: null }, { resetPage: false });
            return;
        }

        if (selectedDuration && !availableDurations.includes(selectedDuration)) {
            updateParams({ duration: null }, { resetPage: false });
        }
    }, [
        availableDurations,
        compareFilters,
        compareMode,
        durationHorizonMap,
        selectedDuration,
        selectedHorizonSeconds,
        selectedStrategyId,
        updateParams
    ]);

    useEffect(() => {
        setPinnedRows((previousPinnedRows) =>
            previousPinnedRows.map((pinnedRow) => rows.find((row) => keyForRow(row) === keyForRow(pinnedRow)) ?? pinnedRow)
        );
    }, [rows]);

    const togglePinnedRow = useCallback((row: CompareRow) => {
        setPinnedRows((previousPinnedRows) => {
            const rowKey = keyForRow(row);
            const existingIndex = previousPinnedRows.findIndex((existingRow) => keyForRow(existingRow) === rowKey);

            if (existingIndex >= 0) {
                return previousPinnedRows.filter((existingRow) => keyForRow(existingRow) !== rowKey);
            }

            return [...previousPinnedRows, row];
        });
    }, []);

    if (strategiesQuery.isLoading && !strategiesQuery.data) {
        return (
            <UserLayout title="Experiment Compare - Project 314">
                <DataLoadingSpinner text="Loading strategies..." />
            </UserLayout>
        );
    }

    if (error && !strategiesQuery.data) {
        return (
            <UserLayout title="Experiment Compare - Error - Project 314">
                <div className="container mx-auto px-4">
                    <div className="mt-6 rounded border border-red-800 bg-red-950/40 px-4 py-3 text-red-200">{error}</div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title="Experiment Compare - Project 314">
            <div className="container mx-auto px-4 py-4">
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                        <Button size="sm" variant="outline-dark" onClick={() => navigate(backToExperimentsUrl)}>
                            <span className="inline-flex items-center whitespace-nowrap">
                                <FaArrowLeft className="mr-2" />
                                Back to Experiments
                            </span>
                        </Button>
                        <div>
                            <h3 className="text-xl font-semibold text-white">Compare Experiment Results</h3>
                            <p className="text-sm text-zinc-400">
                                Rank completed experiments across all strategies or narrow the scope to one strategy. Use fixed horizons for
                                strict apples-to-apples ranking or representative horizons for broader scans.
                            </p>
                        </div>
                    </div>
                    <Card responsivePadding className="w-full max-w-md bg-zinc-900">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Scope</div>
                                <div className="mt-1 text-sm text-white">
                                    {selectedStrategy ? formatStrategyLabel(selectedStrategy.id, selectedStrategy.name) : 'All strategies'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Completed</div>
                                <div className="mt-1 text-sm text-white">
                                    {compareFilters?.totalCompletedExperiments?.toLocaleString() ?? 0} experiments
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                <CompareFiltersCard
                    availableDurations={availableDurations}
                    availableHorizonsForDuration={availableHorizonsForDuration}
                    pageState={pageState}
                    searchParams={searchParams}
                    updateParams={updateParams}
                />

                {pinnedRows.length > 0 && (
                    <PinnedComparisonCard
                        pinnedRows={pinnedRows}
                        selectedStrategyId={selectedStrategyId}
                        togglePinnedRow={togglePinnedRow}
                    />
                )}

                <PerformanceTableCard
                    compareFilters={compareFilters}
                    compareFiltersLoading={compareFilterOptionsQuery.isLoading}
                    compareMode={compareMode}
                    currentPage={currentPage}
                    defaultSortBy={defaultSortBy}
                    error={error}
                    hasMore={compareQuery.data?.hasMore ?? false}
                    hiddenColumns={hiddenColumns}
                    navigate={navigate}
                    pinnedRows={pinnedRows}
                    rows={rows}
                    rowsLoading={compareQuery.isLoading}
                    selectedStrategyId={selectedStrategyId}
                    selectedStrategyQuery={selectedStrategyQuery}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    togglePinnedRow={togglePinnedRow}
                    totalRows={compareQuery.data?.total ?? 0}
                    updateParams={updateParams}
                />
            </div>
        </UserLayout>
    );
};

export default ExperimentCompare;
